import React, { useState } from "react";
import { ShoppingBag } from "lucide-react";
import { PHOTO_COLLECTION } from "@/data/photoCollection";
import { useApp } from "@/context/AppContext";
import { useReveal } from "@/hooks/useReveal";
import { toast } from "sonner";

const STRAP_LABEL = {
  de: { leather: "Leder", steel: "Edelstahl" },
  en: { leather: "Leather", steel: "Steel" },
};

const Collection = () => {
  const { t, lang, addToCart } = useApp();
  const ref = useReveal();

  // One selected strap material ("leather" | "steel") per product id.
  const [strapChoice, setStrapChoice] = useState(() =>
    Object.fromEntries(PHOTO_COLLECTION.map((p) => [p.id, p.defaultStrap || "leather"]))
  );

  const handleAdd = (p) => {
    const strap = strapChoice[p.id] || p.defaultStrap || "leather";
    addToCart({
      type: "collection",
      name: `${p.name} · ${STRAP_LABEL[lang][strap]}`,
      price: p.price,
      quantity: 1,
      image: p.image,
      strap,
    });
    toast.success(lang === "de" ? "Zum Warenkorb hinzugefügt" : "Added to cart");
  };

  return (
    <main ref={ref} data-testid="collection-page" className="pt-32 pb-24 min-h-screen bg-[#170F09]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="mb-20 max-w-3xl reveal">
          <div className="kicker kicker-eyebrow mb-4">{t.collection.kicker}</div>
          <h1 className="section-heading text-4xl md:text-5xl lg:text-6xl mb-6">
            {lang === "de" ? "Direkt aus dem Atelier" : "Straight from the atelier"}
          </h1>
          <p className="text-white/55">
            {lang === "de"
              ? "Vollständig zusammengesetzt. Versand innerhalb von 48 Stunden."
              : "Fully assembled. Ships within 48 hours."}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          {PHOTO_COLLECTION.map((p, i) => (
            <div
              key={p.id}
              data-testid={`photo-collection-${p.id}`}
              className="group card-lift bg-white/[0.015] border border-white/8 transition-colors reveal flex flex-col h-full"
            >
              <div className="relative aspect-square bg-white flex items-center justify-center overflow-hidden p-4 border-b border-white/8">
                <span className="absolute top-3 left-3 z-10 font-mono text-xs tracking-widest text-[#170F09]/30">{String(i + 1).padStart(2, "0")}</span>
                <img
                  src={p.image}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <div className="flex justify-between items-start mb-3 gap-3">
                  <div>
                    <div className="font-heading text-xl text-white">{p.name}</div>
                    <div className="kicker mt-1">{p.tagline[lang]}</div>
                  </div>
                  <div className="font-mono text-[#C9A36B] text-lg shrink-0">CHF {p.price}</div>
                </div>
                <p className="text-white/50 text-sm leading-relaxed mb-5">
                  {p.description[lang]}
                </p>

                <div className="mt-auto">
                  <div className="mb-5" data-testid={`strap-select-${p.id}`}>
                    <div className="kicker mb-2 text-[0.6rem]">{t.configurator.strap}</div>
                    <div className="flex gap-2">
                      {["leather", "steel"].map((mat) => (
                        <button
                          key={mat}
                          type="button"
                          data-testid={`strap-option-${p.id}-${mat}`}
                          onClick={() => setStrapChoice((c) => ({ ...c, [p.id]: mat }))}
                          aria-pressed={strapChoice[p.id] === mat}
                          className={`pill-option flex-1 ${strapChoice[p.id] === mat ? "active" : ""}`}
                        >
                          {STRAP_LABEL[lang][mat]}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button
                    data-testid={`add-photo-${p.id}`}
                    onClick={() => handleAdd(p)}
                    className="btn-champagne w-full text-[0.72rem]"
                  >
                    <ShoppingBag size={14} /> {t.configurator.addToCart}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </main>
  );
};

export default Collection;
